import { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Button } from "reactstrap";
import { mergeCarSuggestion } from "../../utils/vehicleApi";
import { successToast, errorToast } from "../../shared/Toast";
import { C } from "../reporting/reportingStyles";

const headerStyle = { borderBottom: `2px solid ${C.borderLight}` };
const bodyStyle = { padding: 24, fontFamily: "'Inter', sans-serif" };
const footerStyle = { borderTop: `1px solid ${C.borderLight}`, gap: 8 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 6, display: "block" };
const inputStyle = { borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, padding: "8px 12px", color: C.text };
const coralBtnStyle = { background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 };
const ghostBtnStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 };

// Merge a customer suggestion into the canonical row — for the duplicates the
// normalizer can't catch, like "Toyata" or an Arabic spelling.
//
// This is the action to reach for on a duplicate, NOT reject. Merging repoints every
// referencing vehicle onto the real row and deletes the pending one, so the customer's
// car silently becomes correct. Rejecting would leave them looking at "Toyata" forever.
const MergeModal = ({ isOpen, toggle, onMerged, suggestion, makes = [], models = [] }) => {
  const [targetId, setTargetId] = useState("");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const isMake = suggestion?.type === "make";

  useEffect(() => {
    if (isOpen) {
      setTargetId("");
      setSearch("");
    }
  }, [isOpen, suggestion]);

  // Candidates exclude the suggestion itself. Models are limited to the same make —
  // merging a Toyota model into a Kia one would be nonsense.
  const candidates = useMemo(() => {
    const pool = isMake
      ? makes
      : models.filter((m) => String(m.carMakeId) === String(suggestion?.carMakeId));
    const q = search.trim().toLowerCase();
    return pool
      .filter((c) => c.id !== suggestion?.id)
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .slice(0, 100);
  }, [isMake, makes, models, suggestion, search]);

  const handleMerge = async () => {
    if (!targetId) return errorToast("Choose what to merge into");

    const target = candidates.find((c) => String(c.id) === String(targetId));
    const affected = suggestion?.customerCount || 0;
    const warning =
      `Merge "${suggestion.name}" into "${target?.name}"?\n\n` +
      `${affected} customer vehicle(s) will be repointed to "${target?.name}", ` +
      `and "${suggestion.name}" will be deleted.` +
      (isMake ? `\n\nAny models under it will move across too.` : "");
    if (!window.confirm(warning)) return;

    setSaving(true);
    const result = await mergeCarSuggestion(suggestion.id, {
      type: suggestion.type,
      targetId: Number(targetId),
    });
    setSaving(false);

    if (result) {
      successToast(`Merged — ${result.vehiclesUpdated ?? 0} vehicle(s) updated`);
      toggle();
      onMerged?.();
    }
  };

  if (!suggestion) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle} style={headerStyle}>Merge "{suggestion.name}"</ModalHeader>
      <ModalBody style={bodyStyle}>
        <div style={{
          background: C.blueLight, border: `1px solid ${C.blue}33`, borderRadius: 8,
          padding: "10px 12px", fontSize: 12, color: C.textMed, marginBottom: 16,
        }}>
          Use this for duplicates and misspellings. Customers keep working — their vehicle
          is repointed to the real {isMake ? "make" : "model"} without them noticing.
        </div>

        <div style={{ fontSize: 13, color: C.text, marginBottom: 16 }}>
          <strong>{suggestion.customerCount || 0}</strong> customer vehicle(s) currently use this
          {suggestion.makeName && <> · under <strong>{suggestion.makeName}</strong></>}
        </div>

        <label style={labelStyle}>Merge into</label>
        <Input style={{ ...inputStyle, marginBottom: 8 }} value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${isMake ? "makes" : "models"}…`} />
        <Input type="select" style={inputStyle} value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          size={Math.min(Math.max(candidates.length, 3), 10)}>
          {candidates.length === 0 && <option value="" disabled>No matches</option>}
          {candidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Input>
      </ModalBody>
      <ModalFooter style={footerStyle}>
        <Button type="button" onClick={toggle} style={ghostBtnStyle}>Cancel</Button>
        <Button type="button" onClick={handleMerge} disabled={saving || !targetId} style={coralBtnStyle}>
          {saving ? "Merging…" : "Merge"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MergeModal;
